import AdyenCheckout from "@adyen/adyen-web";
import Velocity from "libs/velocity";
import {validateForm, validateField, rerenderValidation} from "../../utils/validation";
import {vehicleReportAddToCart} from "../../utils/purchase-tracking";
import {getPrice} from "../../utils/currency";
import {debounce} from "../../utils/function";
import {trans} from "../../utils/i18n";
import * as addressConsts from "../../consts/address";
import * as creditConsts from "../../consts/credits";
import * as payment from "../../libs/jquery.payment.min.js";
import "libs/select2";
import state from "./PaymentState.js";

const $document = $(document); // TODO: create utils/dom and put $window and $document.
const FORM_SELECTOR = ".js-paymentContainer";
const SELECT_ADDR_SELECTOR = ".js-selectAddress";
const COUNTRY_SELECTOR = "#billingInfo-country";
const ZIP_SELECTOR = "#billingInfo-zip";
const IS_ORGANIZATION_SELECTOR = "#is-organization-yes";
const IS_NOT_ORGANIZATION_SELECTOR = "#is-organization-no";
const VAT_ID_SELECTOR = "#billingInfo-vatId";
const CREDITS_SELECTOR = "#credits-web, #credits-decode, #credits-stolen, #credits-oem-vin-lookup";
const PRICE_SELECTOR = ".js-creditsPrice";
const VAT_PERCENTAGE_SELECTOR = ".js-creditsVatPercentage";
const VAT_SELECTOR = ".js-creditsVat";
const VAT_IS_REVERSE_CHARGE_SELECTOR = ".js-creditsVatIsReverseCharge";
const VAT_FIELD_SELECTOR = ".js-vatIdField";
const TOTAL_PRICE_SELECTOR = ".js-creditsTotalPrice";
const PAYMENT_TYPE_SELECTOR = ".js-paymentType";
const PAYMENT_BUTTONS_SELECTOR = ".js-paymentButton";
const CARD_HOLDER_SELECTOR = "#paymentForm-holderName";
const FORM_VALIDATION_ERROR_SELECTOR = ".js-formValidationError";
const ADYEN_CARD_CONTAINER = "#adyenCardForm-container";
const ADYEN_FIELD_CONTAINER = ".AdyenPaymentField";

const PAYMENT_TYPE_CARD = "Card";
const PAYMENT_TYPE_PAYPAL = "PayPal";

const CARD_HOLDER_NOT_VALID = trans("frontendValidation.Card holder name is not valid");
const CARD_NUMBER_NOT_VALID = trans("frontendValidation.Card number is not valid");
const CVC_NOT_VALID = trans("frontendValidation.Security code is not valid");
const EXPIRY_DATE_NOT_VALID = trans("frontendValidation.Expiry date is not valid");

const creditsConstraints = {
    creditsWeb: creditsValidator(),
    creditsDecode: creditsValidator(),
    creditsStolen: creditsValidator(),
    creditsOemVinLookup: creditsValidator(),
    creditsVehicleMarketValue: creditsValidator(),
    virtualHasCredits: {
        greaterThanZeroAny: {
            keys: ["creditsDecode", "creditsStolen", "creditsOemVinLookup", "creditsVehicleMarketValue"]
        }
    }
};

const billingInfoConstraints = {
    country: {
        presence: true
    },
    state: {
        presence: true
    },
    first_name: {
        presence: true,
        length: {
            minimum: 2,
            maximum: 50
        }
    },
    last_name: {
        presence: true,
        length: {
            minimum: 2,
            maximum: 50
        }
    },
    organization: {
        presence: true,
        length: {
            minimum: 1,
            maximum: 50,
        }
    },
    url: {
        url: true
    },
    street: {
        presence: true,
        length: {
            minimum: 1,
            maximum: 100,
        }
    },
    zip: {
        presence: true,
        format: {
            pattern: "[a-zA-Z0-9- ]+"
        },
        length: {
            minimum: 4,
            maximum: 10
        }
    },
    city: {
        presence: true
    },
    email: {
        presence: true,
        email: true,
    },
    company_id: {
        length: {
            maximum: 40,
        }
    }
};

const vatIdConstraints = {
    country: {
        presence: true,
    },
    vat_id: {
        vatIdFormat: true,
    }
};

const termsAndConditions = {
    agreed_tac: {
        checked: {
            message: function () {
                return trans("my.You have to agree to the Terms and Conditions and Privacy Policy");
            }
        }
    }
};

const STOLEN_CHECK_CREDITS = "creditsStolen";
const OEM_VIN_LOOKUP_CREDITS = "creditsOemVinLookup";
const VIN_DECODE_CREDITS = "creditsDecode";
const VEHICLE_MARKET_VALUE_CREDITS = "creditsVehicleMarketValue";

const creditInputs = {};
creditInputs[STOLEN_CHECK_CREDITS] = "#creditsStolen";
creditInputs[OEM_VIN_LOOKUP_CREDITS] = "#creditsOemVinLookup";
creditInputs[VIN_DECODE_CREDITS] = "#creditsDecode";
creditInputs[VEHICLE_MARKET_VALUE_CREDITS] = "#creditsVehicleMarketValue";

const creditTypeToFieldName = {
    [creditConsts.TYPE_WEB_ALL]: "creditsWeb",
    [creditConsts.TYPE_VEHICLE_REPORT]: "creditsDecode",
    [creditConsts.TYPE_API_STOLEN_CHECK]: "creditsStolen",
    [creditConsts.TYPE_API_OEM_VIN_LOOKUP]: "creditsOemVinLookup",
    [creditConsts.TYPE_API_VEHICLE_MARKET_VALUE]: "creditsVehicleMarketValue",
    ["fixedPrice"]: "fixedPrice",
};

// To be filled on init.
let priceTable = {};
let trackedAddingToCart = false;

const adyenConfiguration = {
    locale: "en-US",
    environment: "live",
    clientKey: "",
};

// Prevents bad UI change.
const debouncedRecalculateVatRate = debounce(recalculateVatRate, 500, {leading: false});
const debouncedRecalculateVatRateByAddress = debounce(recalculateVatRateByAddress, 500, {leading: false});

window.encryptedCardData = "";

export default function initPayment() {
    const constraints = {...creditsConstraints, ...billingInfoConstraints, ...vatIdConstraints, ...termsAndConditions};
    $("[data-vd-select-country]").select2();
    $document

        // Handle conditional form fields for some contries.
        .on("change", COUNTRY_SELECTOR, function() {
            const countryCode = $(this).val();
            shouldShowStates(countryCode);
            debouncedRecalculateVatRate(); // Todo: hint here
        })
        // Handle existing address selecting.
        .on("click", SELECT_ADDR_SELECTOR, function () {
            const $this = $(this);
            const id = parseInt($this.data("address-id"), 10);
            const isNew = id === 0;

            state.addressId = id;

            if (isNew) {
                enableAddressValidation();

                debouncedRecalculateVatRate();
            } else {
                disableAddressValidation();

                debouncedRecalculateVatRateByAddress();
            }

            renderAddresses();
        })

        // Handle conditional form fields for some contries.
        .on("change", ZIP_SELECTOR, function () {
            debouncedRecalculateVatRate();
        })
        // Handle conditional form fields if an organization.
        .on("change", IS_ORGANIZATION_SELECTOR, function () {
            enableOrganizationValidation();
            debouncedRecalculateVatRate();
        })
        // Handle conditional form fields if NOT an organization.
        .on("change", IS_NOT_ORGANIZATION_SELECTOR, function () {
            disableOrganizationValidation();
            debouncedRecalculateVatRate();
        })
        // Handle VAT calculation when leaving VAT ID field.
        .on("blur", VAT_ID_SELECTOR, function () {
            debouncedRecalculateVatRate();
        })
        // Calculate prices
        .on("input", CREDITS_SELECTOR, function () {
            fetchCreditsFromInput(this);
        })
        .on("change", PAYMENT_TYPE_SELECTOR, function () {
            showPaymentButton();
        })
        // Card Holder validation
        .on('input', CARD_HOLDER_SELECTOR, function () {
            if ($(this).closest(".FormControlBox").hasClass("is-error")) {
                cardHolderValidator(this.value);
            }
        })
        .on('blur', CARD_HOLDER_SELECTOR, function () {
            cardHolderValidator(this.value);
        })
        // Form validation
        .on("change input", FORM_SELECTOR, function (e) {
            // Don't validate inputs without name attr (e.g. credit card inputs)
            if (!$(e.target).attr("name")) {
                return;
            }

            const errors = validateField(this, e.target, constraints, state.whitelist);

            renderVirtualErrors(errors);
        })
        .on("click", FORM_SELECTOR, function (e) {
            if (trackedAddingToCart) {
                return;
            }

            trackedAddingToCart = true;

            try {
                const meta = $(FORM_SELECTOR).data("metadata");
                if (meta === undefined || meta.length === 0 || !meta.hasOwnProperty("vin")) {
                    return;
                }

                vehicleReportAddToCart(meta.vin, meta.price, meta.currencyCode, meta.make, meta.model);
            } catch (e) {
            }
        })
        .on('click', PAYMENT_BUTTONS_SELECTOR, function () {
            // If is used on pricing page
            if (state.isPricingPage) {
                // Populate credits
                Object.keys(state.creditsSelected).forEach(key => {
                    setInputAmount(state.creditsSelected[key], key);
                });

                // Populate credit packs
                Object.keys(state.creditPacksSelected).forEach(key => {
                    setInputAmount(state.creditPacksSelected[key].amount, key);
                });

                // F**k it from above!!!
                state.whitelist.creditsDecode = false;
                state.whitelist.creditsWeb = false;
                state.whitelist.creditsStolen = false;
                state.whitelist.creditsOemVinLookup = false;
                state.whitelist.creditsVehicleMarketValue = false;
            }

            let $this = $(this);
            let $form = $(FORM_SELECTOR);

            $this
                .addClass("is-disabled")
                .attr("disabled", true);

            // Validate form
            $(FORM_VALIDATION_ERROR_SELECTOR)
                .addClass("u-isHidden")
                .html("");

            const errors = validateForm($form, constraints, state.whitelist);
            renderVirtualErrors(errors);

            if (errors || !isCreditCardFormValid()) {
                $(`${FORM_SELECTOR} [class*=rror]`).each(function (i, e) {
                    if (!e.classList.contains("u-isHidden")) {
                        Velocity($(e), "scroll", {duration: 500, easing: "swing"});
                        return false;
                    }
                });

                $this
                    .removeClass("is-disabled")
                    .removeAttr("disabled");

                return;
            }

            // Create hidden input with encrypted payment data - do it like this, so browser doesn't cache data in the input after refresh
            const $encryptedCardData = $(`<input type="hidden" name="encrypted_card_data">`);
            $encryptedCardData.val(encryptedCardData);
            $form.append($encryptedCardData);

            // Submit form
            if (state.isPricingPage) {
                sessionStorage.setItem('paymentInProgress', 'true');
            }
            $form.submit();
        })
}


/**
 * Initialization function.
 * @param {Object} options
 * @param {Object} options.locale - full locale, e.g. "en_US"
 * @param {Object} options.adyenEnv - Adyen environment (test/live)
 * @param {Object} options.adyenClientKey - Adyen Public key
 * @param {Object} options.whitelist - initial validation whitelist, gets merged to default one.
 * @param {int} options.addressId
 * @param {boolean} options.withStates
 * @param {boolean} options.withZip
 * @param {boolean} options.priceTable
 * @param {string} options.currencyCode
 * @param {boolean} options.isOrganization
 * @param {boolean} options.unknownUser
 * @param {array} options.creditsSelected
 * @param {array} options.creditPacksSelected
 */
export function init(options = {}) {
    adyenConfiguration.locale = options.locale;
    adyenConfiguration.environment = options.adyenEnv;
    adyenConfiguration.clientKey = options.adyenClientKey;

    initializeAdyenCard();
    try {
        Object.assign(state.whitelist, options.whitelist);
        Object.assign(state.creditsSelected, options.creditsSelected);
        Object.assign(state.creditPacksSelected, options.creditPacksSelected);
    } catch (e) {
        // IE11 workaround
        for (var i in options.whitelist) {
            state.whitelist[i] = options.whitelist[i];
        }
        for (var i in options.creditsSelected) {
            state.creditsSelected[i] = options.creditsSelected[i];
        }
        for (var i in options.creditPacksSelected) {
            state.creditPacksSelected[i] = options.creditPacksSelected[i];
        }
    }

    state.addressId = options.addressId;
    state.withStates = options.withStates;
    state.withZip = options.withZip;

    if (options.isOrganization) {
        enableOrganizationValidation();
    } else {
        disableOrganizationValidation();
    }

    if (options.unknownUser) {
        enableEmailValidation();
    } else {
        disableEmailValidation();
    }

    const hasAddressId = options.addressId !== 0;

    if (hasAddressId) {
        disableAddressValidation();
        recalculateVatRateByAddress();
    } else {
        enableAddressValidation();
        recalculateVatRate();
    }

    if (options.currencyCode) {
        state.currencyCode = options.currencyCode;
    }

    // Transform price rows to a better data structure.
    priceTable = options.priceTable.reduce(function (acc, priceRow) {
        const fieldName = creditTypeToFieldName[priceRow.creditType];

        if (!acc[fieldName]) {
            acc[fieldName] = [];
        }

        acc[fieldName].push(priceRow);

        return acc;
    }, {});

    if (state.whitelist.fixedPrice) {
        state.prices["fixedPrice"] = calculatePrice("fixedPrice", 1);
    }

    // Populate prices from whitelist #yolo
    Object.keys(creditTypeToFieldName).forEach(key => {
        const value = creditTypeToFieldName[key];
        if (state.whitelist[value]) {
            state.prices[value] = calculatePrice(value, 1);

            // Populate internal list of purchased credits from inputs
            let input = document.getElementsByName(value)[0];
            if (typeof input !== "undefined") {
                fetchCreditsFromInput(input);
            }
        }
    });

    showPaymentButton();

    // When loaded on vin decode, init price from BE and show it, hate it
    state.noInputs = options.noInputs !== undefined;
    state.isPricingPage = options.isPricingPage !== undefined;

    // Show States if Country set on page load needs it
    shouldShowStates($(COUNTRY_SELECTOR).val());
}

async function initializeAdyenCard() {
    const adyenCheckout = await AdyenCheckout(adyenConfiguration);
    const adyenCardForm = adyenCheckout.create('securedfields', {
        type: 'card',
        hasHolderName: true,
        holderNameRequired: true,

        onChange: handleAdyenOnChange,
        onBrand: handleAdyenOnBrand,

        styles: {
            base: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                fontWeight: 200,
                color: '#3e4b54',
            },
            error: {
                // color: '#f92805'
            },
            validated: {
                // color: '#00cc00'
            },
            placeholder: {
                color: '#738a9c'
            }
        },
    }).mount(ADYEN_CARD_CONTAINER);
}

/**
 *
 * @param state
 * @param component
 */
function handleAdyenOnChange(state, component) {

    // Card Number valid
    if (state.errors.hasOwnProperty("encryptedCardNumber") && state.errors.encryptedCardNumber === false) {
        renderValidationForAdyenField("encryptedCardNumber", state.valid.encryptedCardNumber === true);
    }

    // CVV valid
    if (state.errors.hasOwnProperty("encryptedSecurityCode") && state.errors.encryptedSecurityCode === false) {
        renderValidationForAdyenField("encryptedSecurityCode", state.valid.encryptedSecurityCode === true);
    }

    // Expiry valid
    if (state.errors.hasOwnProperty("encryptedExpiryDate") && state.errors.encryptedExpiryDate === false) {
        renderValidationForAdyenField("encryptedExpiryDate", (state.valid.encryptedExpiryMonth === true && state.valid.encryptedExpiryYear === true));
    }

    if (state.isValid) {
        window.encryptedCardData = JSON.stringify(state.data);
    } else {
        window.encryptedCardData = "";
    }
}

function handleAdyenOnBrand(brand) {
    document.querySelector(".js-cardIcon").setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#card-${brand.brand}`);
}

/**
 * @param {string} fieldType One of [encryptedCardNumber|encryptedExpiryDate|encryptedSecurityCode]
 * @param {boolean} isValid
 * @param {null|string} customError
 */
export function renderValidationForAdyenField(fieldType, isValid, customError = null) {
    const $fieldContainer = $(`${ADYEN_FIELD_CONTAINER}--${fieldType}`);
    if (isValid) {
        $fieldContainer
            .addClass("is-ok")
            .removeClass("is-erroneous")
        ;

        rerenderValidation($fieldContainer, null);
    } else {
        $fieldContainer
            .addClass("is-erroneous")
            .removeClass("is-ok")
        ;

        let errorText = "";
        if (typeof customError !== "undefined" && customError !== null && customError.length > 0) {
            errorText = customError;
        } else {
            switch (fieldType) {
                case "encryptedCardNumber":
                    errorText = CARD_NUMBER_NOT_VALID;
                    break;
                case "encryptedExpiryDate":
                    errorText = EXPIRY_DATE_NOT_VALID;
                    break;
                case "encryptedSecurityCode":
                    errorText = CVC_NOT_VALID;
                    break;
                default:
                    errorText = "Unknown error";
            }
        }

        rerenderValidation($fieldContainer, [errorText]);
    }
}

function isCreditCardFormValid() {
    // If not CC payment --> valid
    if (getPaymentType() !== PAYMENT_TYPE_CARD) {
        return true;
    }
    // Check if we have encrypted card data. If not --> invalid
    if (window.encryptedCardData.length === 0) {
        $(FORM_VALIDATION_ERROR_SELECTOR)
            .removeClass("u-isHidden")
            .html(trans("frontendValidation.Card data are not valid"));

        // Add "is-erroneous" class to all fields without "is-ok" class.
        $(ADYEN_FIELD_CONTAINER).each(function (i, el) {
            const $container = $(el);
            const fieldType = $container.find("[data-cse]").data("cse");

            renderValidationForAdyenField(fieldType, $container.hasClass("is-ok"));
        });

        return false;
    } else {
        // Clear error message if present.
        $(FORM_VALIDATION_ERROR_SELECTOR)
            .addClass("u-isHidden")
            .html("");
    }
    // Check if the Card Holder is valid.
    return cardHolderValidator($(CARD_HOLDER_SELECTOR).val());
}

function creditsValidator() {
    return {
        presence: true,
        numericality: {
            onlyInteger: true,
            greaterThan: 0,
            lessThan: creditConsts.MAX_AMOUNT + 1
        }
    };
}

function cardHolderValidator(cardHolder) {
    let error = null;
    if (cardHolder.trim().length < 2) {
        error = [CARD_HOLDER_NOT_VALID];
    }

    rerenderValidation($(CARD_HOLDER_SELECTOR), error);

    return (error === null);
}

function fetchCreditsFromInput(input) {
    const $input = $(input);
    const count = isNaN(parseInt($input.val())) ? 0 : parseInt($input.val(), 10);
    const name = input.name;

    // Update state.
    state.creditsSelected[name] = count;

    renderPrice(name);
    renderTotalPrice();
}

function calculatePrice(name, count) {
    if (priceTable[name]) {
        return priceTable[name].reduce(function (acc, entry) {
            if (entry.currencyCode === state.currencyCode && count >= entry.minAmount && (!entry.maxAmount || count <= entry.maxAmount)) {
                acc += entry.unitPrice * count;
            }

            return acc;
        }, 0);
    }

    return 0;
}

function calculateCreditsPrice(prices) {
    return Object
        .keys(prices)
        .reduce((acc, key) => acc + prices[key], 0);
}

function disableAddressValidation() {
    // Store for simple rollback.
    try {
        state.oldWhitelist = Object.assign({}, state.whitelist);
    } catch (e) {
        // IE11 workaround
        state.oldWhitelist = [];
        for (var i in state.whitelist) {
            state.oldWhitelist[i] = state.whitelist[i];
        }
    }

    Object.keys(billingInfoConstraints).forEach(function (field) {
        if (state.whitelist[field] !== undefined) {
            state.whitelist[field] = false;
        }
    });
}

function enableAddressValidation() {
    // Restore pre-select address validations state.
    try {
        Object.assign(state.whitelist, state.oldWhitelist);
    } catch (e) {
        // IE11 workaround
        for (var i in state.oldWhitelist) {
            state.whitelist[i] = state.oldWhitelist[i];
        }
    }
}

function enableOrganizationValidation() {
    state.whitelist.organization = true;
    state.whitelist.vat_id = false;
    state.whitelist.url = true;
}

function disableOrganizationValidation() {
    state.whitelist.organization = false;
    state.whitelist.vat_id = false;
    state.whitelist.url = false;

    $(VAT_FIELD_SELECTOR).find("input[name=vat_id]").val("");
}

function enableEmailValidation() {
    state.whitelist.email = true;
}

function disableEmailValidation() {
    state.whitelist.email = false;
}

function enableVatIdValidation() {
    state.whitelist.vat_id = true;
}

function disableVatIdValidation() {
    state.whitelist.vat_id = false;
}

function recalculateVatRate() {
    const vatId = $(VAT_ID_SELECTOR).val();
    const countryCode = $(COUNTRY_SELECTOR).val();
    const zipCode = $(ZIP_SELECTOR).val() || null;
    const isOrganization = $(IS_ORGANIZATION_SELECTOR).prop("checked");
    $
        .get("/ajax/vat/data", {
            vatId,
            countryCode,
            zipCode,
            isOrganization
        })
        .then(handleVatDataResponse);
}

function recalculateVatRateByAddress() {
    const addressId = state.addressId;

    $.get(`/ajax/vat/data/address/${addressId}`)
        .then(handleVatDataResponse);
}

function handleVatDataResponse(response) {
    const $vatItSelector = $(VAT_FIELD_SELECTOR).find("label");

    state.vatRate = response.rate;
    state.vatIsReverseCharge = response.vatIsReverseCharge;
    state.showVatIdField = response.showVatIdField;

    switch (response.validVatId) {
        case true:
            rerenderValidation($vatItSelector, null);
            break;
        case false:
            rerenderValidation($vatItSelector, [trans("frontendValidation.Invalid VAT ID")]);
            break;
        default:
            rerenderValidation($vatItSelector, null);
            $vatItSelector.closest(".FormControlBox").removeClass("is-success");
    }

    renderTotalPrice();
    renderVatIdField();
}

function renderVirtualErrors(errors = {}) {
    // Manually handle this error
    if (Array.isArray(errors.virtualHasCredits)) {
        $(".js-hasCreditsError").removeClass("u-isHidden").html(errors.virtualHasCredits[0]);
    } else {
        $(".js-hasCreditsError").addClass("u-isHidden")
    }
}

function renderAddresses() {
    $(".js-addressId").val(state.addressId);

    const newAddress = state.addressId === 0;

    $(".js-addressForm").toggleClass("u-isHidden", !newAddress);
    $(".js-newAddressBtn").toggleClass("u-isHidden", newAddress);

    $(".js-selectAddress").each(function () {
        const $this = $(this);
        const thisId = parseInt($this.data("address-id"), 10);

        $this.toggleClass("is-selected", thisId === state.addressId);
    });
}

function renderZipLabel() {
    const $zipLabel = $(".js-billingInfoZipLabel");

    if (state.withZip) {
        $zipLabel.html($zipLabel.data("html-us"));
    } else {
        $zipLabel.html($zipLabel.data("html-non-us"));
    }
}

function renderVatIdField() {
    const isOrganization = $(IS_ORGANIZATION_SELECTOR).prop("checked");
    const $vatFieldSelector = $(VAT_FIELD_SELECTOR);

    $vatFieldSelector.toggleClass("u-isHidden", !state.showVatIdField);

    if (state.showVatIdField && isOrganization) {
        enableVatIdValidation();
    } else {
        $vatFieldSelector.find("input[name=vat_id]").val("");
        disableVatIdValidation();
    }
}

function renderPrice(name) {
    const priceContainer = $(`.js-${name}Price`);
    const price = getPrice(state.creditsSelected[name] * state.prices[name], state.currencyCode);
    if (priceContainer.length > 0) {
        priceContainer.html(price);
    } else {
        //Fallback
        $(PRICE_SELECTOR).html(price);
    }
}

function renderCredits(lastInput) {
    if (lastInput) {
        $(`[name="${lastInput}"]`)[0].focus();
    }
}

function countCreditPacksPrice() {
    let total = 0;
    Object.keys(state.creditPacksSelected).forEach(key => {
        total += state.creditPacksSelected[key].price;
    });
    return total;
}

function setInputAmount(amount, creditType) {
    $(creditInputs[creditType]).val(amount);
}

export function renderTotalPrice(options = {}) {
    let creditsPrice = 0;

    if (state.noInputs) {
        creditsPrice = calculateCreditsPrice(state.prices);
        $(PRICE_SELECTOR).html(getPrice(creditsPrice, state.currencyCode));
    } else {

        // Calculate selected credit packs
        if (Object.keys(state.creditPacksSelected).length > 0) {
            creditsPrice = countCreditPacksPrice();
        }

        // Calculate all previously selected credits
        let creditPacksPrice = 0;
        Object.keys(state.creditsSelected).forEach(key => {
            creditPacksPrice += state.creditsSelected[key] * state.prices[key];
        });
        creditsPrice += creditPacksPrice;

        //If price is provided
        if (options.price !== undefined) {
            creditsPrice += isNaN(parseFloat(options.price)) ? 0 : parseFloat(options.price);
        }
    }
    const vatRate = state.vatRate;
    const vatIsReverseCharge = state.vatIsReverseCharge;
    const vat = creditsPrice * vatRate;

    $(VAT_PERCENTAGE_SELECTOR).html(`${vatRate * 100}%`);
    $(VAT_SELECTOR).html(getPrice(vat, state.currencyCode));
    $(VAT_IS_REVERSE_CHARGE_SELECTOR).toggleClass("u-isHidden", !vatIsReverseCharge);
    const totalPrice = creditsPrice + vat;
    $(TOTAL_PRICE_SELECTOR).html(getPrice(totalPrice, state.currencyCode));
}

function shouldShowStates(countryCode) {
    state.withZip = addressConsts.COUNTRIES_WITH_ZIP.indexOf(countryCode) > -1;
    renderZipLabel();

    state.withStates = addressConsts.COUNTRIES_WITH_STATES.indexOf(countryCode) > -1;
    renderStates(countryCode);

    // Enable/disable state validation.
    state.whitelist.state = state.withStates;
}

function renderStates(countryCode) {
    const $withState = $(`[data-with-state]`);
    const $withNoState = $(`[data-with-no-state]`);

    $withState.toggleClass("u-isHidden", !state.withStates);
    $withNoState.toggleClass("u-isHidden", state.withStates);

    if (state.withStates) {
        $
            .get(`/ajax/states/${countryCode}`)
            .then(function (response) {
                const $stateSelect = $("#billingInfo-state");

                $stateSelect.find("option").remove();
                $stateSelect.append(response.states.reduce(function (acc, state) {
                    return acc + `<option value="${state.a2}">${state.a2}</option>`;
                }, ""));
                $stateSelect.trigger("change"); // Select2 needs this to change the view.
            });
    }
}

/**
 * Returns selected payment method
 *
 * @returns string
 */
function getPaymentType() {
    return $(PAYMENT_TYPE_SELECTOR).filter(":checked").val();
}

/**
 * Shows payment button based on the selected payment type
 */
function showPaymentButton() {
    $(PAYMENT_BUTTONS_SELECTOR).addClass("u-isHidden");
    $(PAYMENT_BUTTONS_SELECTOR + "-" + getPaymentType()).removeClass("u-isHidden");
}
